import { useCallback, useEffect, useRef } from 'react';
import Image from 'next/image';
import { ChevronLeftIcon, ChevronRightIcon, CloseIcon } from '@eventbrite/marmalade';
import type { OrganizerMedia } from '@/types/organizer';
import styles from './MediaLightbox.module.scss';

interface Props {
  medias: OrganizerMedia[];
  activeIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

export function MediaLightbox({ medias, activeIndex, onClose, onNavigate }: Props) {
  const thumbnailRef = useRef<HTMLButtonElement>(null);

  const goNext = useCallback(() => {
    if (activeIndex < medias.length - 1) onNavigate(activeIndex + 1);
  }, [activeIndex, medias.length, onNavigate]);

  const goPrev = useCallback(() => {
    if (activeIndex > 0) onNavigate(activeIndex - 1);
  }, [activeIndex, onNavigate]);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose, goNext, goPrev]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    thumbnailRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }, [activeIndex]);

  const current = medias[activeIndex];

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-label="Media viewer">
      <div className={styles.header}>
        <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close">
          <CloseIcon />
        </button>
      </div>

      <div className={styles.viewer}>
        <button
          type="button"
          className={styles.navButton}
          onClick={goPrev}
          disabled={activeIndex === 0}
          aria-label="Previous"
        >
          <ChevronLeftIcon />
        </button>

        <div className={styles.mainImageWrapper} key={current.imageId}>
          <Image
            className={styles.mainImage}
            src={current.url}
            alt=""
            fill
            sizes="100vw"
            priority
          />
        </div>

        <button
          type="button"
          className={styles.navButton}
          onClick={goNext}
          disabled={activeIndex === medias.length - 1}
          aria-label="Next"
        >
          <ChevronRightIcon />
        </button>
      </div>

      <div className={styles.thumbnails}>
        {medias.map((media, i) => (
          <button
            key={media.imageId}
            type="button"
            ref={i === activeIndex ? thumbnailRef : undefined}
            className={`${styles.thumbnail} ${i === activeIndex ? styles.thumbnailActive : ''}`}
            onClick={() => onNavigate(i)}
            aria-label={`View media ${i + 1}`}
          >
            <Image src={media.url} alt="" fill sizes="(max-width: 639px) 5rem, 8.75rem" />
          </button>
        ))}
      </div>
    </div>
  );
}
